import { range } from 'ramda'
import { useMemo } from 'react'

// Placeholder for dots
const DOTS = -1 as const
const FIRST_PAGE = 0 as const
// Count of the pages to be shown next to currently selected page (default: 1)
const NEIGHBOUR_PAGES = 1 as const

/**
 * @param currentPage Currently selected page
 * @param totalPages Total count of pages
 * @param showOnlyCurrentPage If true, only the currently selected page will be shown
 * @returns Generated pages range with dots. `-1` in the array is used as a placeholder for dots. Array is indexed from 0.
 */
const usePagesRange = (currentPage: number, totalPages: number, showOnlyCurrentPage: boolean) => {
  const rangeWithDots = useMemo(() => {
    const lastPageIndex = totalPages - 1
    if (showOnlyCurrentPage) {
      return [currentPage]
    }
    if (lastPageIndex < 5) {
      return range(FIRST_PAGE, lastPageIndex + 1)
    }
    const lowerBound = Math.max(FIRST_PAGE, currentPage - NEIGHBOUR_PAGES)
    const upperBound = Math.min(lastPageIndex, currentPage + NEIGHBOUR_PAGES)

    const showLeftDots = lowerBound > 1
    const showRightDots = upperBound < lastPageIndex - 1

    if (!showLeftDots && showRightDots) {
      const leftItems = NEIGHBOUR_PAGES * 2 + 2
      const leftRange = range(FIRST_PAGE, leftItems)

      return [...leftRange, DOTS, lastPageIndex]
    }

    if (showLeftDots && !showRightDots) {
      const rightItems = NEIGHBOUR_PAGES * 2 + 2
      const rightRange = range(totalPages - rightItems, totalPages)
      return [FIRST_PAGE, DOTS, ...rightRange]
    }

    if (showLeftDots && showRightDots) {
      const middleRange = range(lowerBound, upperBound + 1)
      return [FIRST_PAGE, DOTS, ...middleRange, DOTS, lastPageIndex]
    }
    return []
  }, [currentPage, showOnlyCurrentPage, totalPages])

  return { pages: rangeWithDots }
}

export default usePagesRange
